import React, { useState, useCallback, useRef } from 'react';
import { Modal, Button, Input, message, Alert, Spin, Descriptions } from 'antd';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { SearchOutlined, ImportOutlined, CompassOutlined } from '@ant-design/icons';

const mapContainerStyle = {
    width: '100%',
    height: '450px',
    borderRadius: '12px',
};

// Default center (Hyderabad)
const defaultCenter = {
    lat: 17.3850,
    lng: 78.4867,
};

const libraries = ['places'];

const FetchBloodBankMapModal = ({ open, onCancel, onImport, apiKey, title = "Fetch Blood Banks from Google Maps", defaultQuery = "Blood Bank" }) => {
    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: apiKey,
        libraries,
    });

    const [map, setMap] = useState(null);
    const [center, setCenter] = useState(defaultCenter);
    const [searchCity, setSearchCity] = useState('');
    const [markers, setMarkers] = useState([]);
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');

    const onLoad = useCallback((map) => {
        setMap(map);
    }, []);

    const onUnmount = useCallback((map) => {
        setMap(null);
    }, []);

    const handleSearch = () => {
        if (!searchCity) return;
        setIsLoading(true);
        setLoadingMessage('Locating city...');

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: searchCity }, (results, status) => {
            if (status === 'OK' && results[0]) {
                const location = results[0].geometry.location;
                setCenter({ lat: location.lat(), lng: location.lng() });
                if (map) {
                    map.panTo(location);
                    map.setZoom(12);
                }
                setIsLoading(false);
                setLoadingMessage('');
            } else {
                message.error('City not found: ' + status);
                setIsLoading(false);
            }
        });
    };

    const handleFetchBloodBanks = () => {
        if (!map) return;
        setIsLoading(true);
        setLoadingMessage(`Searching for ${defaultQuery} in this area...`);

        const service = new window.google.maps.places.PlacesService(map);
        const request = {
            location: map.getCenter(),
            radius: '5000', // 5km radius
            query: defaultQuery,
            // type: 'health' // Optional, but query is usually better
        };

        service.textSearch(request, (results, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
                const newMarkers = results.map(place => ({
                    id: place.place_id,
                    position: place.geometry.location,
                    name: place.name,
                    address: place.formatted_address,
                    rating: place.rating,
                    place_id: place.place_id,
                    user_ratings_total: place.user_ratings_total,
                    // Note: Phone number requires getDetails per place, which is expensive on quota.
                    // We will import available data.
                }));
                setMarkers(newMarkers);
                message.success(`Found ${newMarkers.length} results!`);

                // Fit bounds to show all markers
                if (newMarkers.length > 0) {
                    const bounds = new window.google.maps.LatLngBounds();
                    newMarkers.forEach(marker => bounds.extend(marker.position));
                    map.fitBounds(bounds);
                }

            } else {
                message.warning('No blood banks found in this area.');
                setMarkers([]);
            }
            setIsLoading(false);
            setLoadingMessage('');
        });
    };

    const handleImport = () => {
        if (markers.length === 0) {
            message.warning('No blood banks to import.');
            return;
        }
        onImport(markers);
    };

    if (loadError) return <Alert message="Error loading maps" type="error" />;

    return (
        <Modal
            title={title}
            open={open}
            onCancel={onCancel}
            width={900}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button
                    key="import"
                    type="primary"
                    icon={<ImportOutlined />}
                    onClick={handleImport}
                    disabled={markers.length === 0 || isLoading}
                >
                    Import {markers.length > 0 ? `${markers.length} Found` : ''}
                </Button>
            ]}
            centered
        >
            <div className="d-flex gap-2 mb-3">
                <Input
                    placeholder="Enter City (e.g., Hyderabad)"
                    value={searchCity}
                    onChange={(e) => setSearchCity(e.target.value)}
                    onPressEnter={handleSearch}
                    style={{ flex: 1 }}
                />
                <Button
                    type="default"
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                    loading={isLoading && loadingMessage === 'Locating city...'}
                >
                    Locate City
                </Button>
                <Button
                    type="primary"
                    icon={<CompassOutlined />}
                    onClick={handleFetchBloodBanks}
                    loading={isLoading && loadingMessage.startsWith('Searching')}
                >
                    Search This Area
                </Button>
            </div>

            <Spin spinning={isLoading} tip={loadingMessage}>
                {isLoaded ? (
                    <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={center}
                        zoom={12}
                        onLoad={onLoad}
                        onUnmount={onUnmount}
                        options={{
                            disableDefaultUI: false,
                            zoomControl: true,
                            streetViewControl: false,
                            mapTypeControl: false,
                        }}
                    >
                        {markers.map((marker) => (
                            <Marker
                                key={marker.id}
                                position={marker.position}
                                onClick={() => setSelectedPlace(marker)}
                            />
                        ))}

                        {selectedPlace && (
                            <InfoWindow
                                position={selectedPlace.position}
                                onCloseClick={() => setSelectedPlace(null)}
                            >
                                <div style={{ maxWidth: '250px' }}>
                                    <h6 style={{ marginBottom: '5px' }}>{selectedPlace.name}</h6>
                                    <p style={{ fontSize: '12px', margin: 0, color: '#666' }}>
                                        {selectedPlace.address}
                                    </p>
                                    <div style={{ marginTop: '5px', fontSize: '12px' }}>
                                        {selectedPlace.rating &&
                                            <span style={{ color: '#fadb14', marginRight: '8px' }}>
                                                ★ {selectedPlace.rating} ({selectedPlace.user_ratings_total})
                                            </span>
                                        }
                                    </div>
                                </div>
                            </InfoWindow>
                        )}
                    </GoogleMap>
                ) : (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '450px', background: '#f5f5f5', borderRadius: '12px' }}>
                        Loading Maps...
                    </div>
                )}
            </Spin>
        </Modal>
    );
};

export default FetchBloodBankMapModal;
